import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuditOutcome, AuditPage } from '../api/types';
import { useApi, useDebounced } from '../lib/use-api';
import { formatDateTime } from '../lib/format';
import { EmptyNotice, ErrorNotice, Loading } from '../components/Feedback';
import { Pagination } from '../components/Pagination';

const PAGE_SIZE = 50;
const OUTCOMES: readonly AuditOutcome[] = ['success', 'denied', 'error'];

/**
 * Nhật ký kiểm toán — plan §5.4.
 *
 * Chỉ đọc. Không có nút sửa hay xóa ở đây, và cũng không có endpoint tương ứng
 * ở backend: nhật ký sửa được thì không còn là nhật ký.
 *
 * `action` hiển thị NGUYÊN mã (`lti_platform.disable`) chứ không dịch. Đây là
 * dữ liệu đối soát, và người đọc nó cần thấy đúng chuỗi có trong DB để tra
 * chéo với log của hệ thống khác.
 */
export function AuditLogPage(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.resolvedLanguage ?? 'vi';

  const [action, setAction] = useState('');
  const [outcome, setOutcome] = useState<AuditOutcome | ''>('');
  const [offset, setOffset] = useState(0);

  const debouncedAction = useDebounced(action);

  const query = useMemo(
    () => ({ action: debouncedAction, outcome, limit: PAGE_SIZE, offset }),
    [debouncedAction, outcome, offset],
  );
  const page = useApi<AuditPage>('/api/audit', { query });

  return (
    <section>
      <h1>{t('audit.heading')}</h1>

      <div className="toolbar toolbar--wrap">
        <label className="field field--inline">
          <span>{t('audit.filter.action')}</span>
          <input
            type="search"
            value={action}
            onChange={(event) => {
              setAction(event.target.value);
              setOffset(0);
            }}
          />
        </label>

        <label className="field field--inline">
          <span>{t('audit.filter.outcome')}</span>
          <select
            value={outcome}
            onChange={(event) => {
              setOutcome(event.target.value as AuditOutcome | '');
              setOffset(0);
            }}
          >
            <option value="">{t('audit.filter.allOutcomes')}</option>
            {OUTCOMES.map((value) => (
              <option key={value} value={value}>
                {t(`audit.outcome.${value}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {page.loading ? <Loading /> : null}
      {page.error ? <ErrorNotice error={page.error} onRetry={page.reload} /> : null}

      {page.data && !page.loading ? (
        page.data.rows.length === 0 ? (
          <EmptyNotice />
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t('audit.column.time')}</th>
                  <th scope="col">{t('audit.column.actor')}</th>
                  <th scope="col">{t('audit.column.action')}</th>
                  <th scope="col">{t('audit.column.target')}</th>
                  <th scope="col">{t('audit.column.outcome')}</th>
                  <th scope="col">{t('audit.column.ipAddress')}</th>
                </tr>
              </thead>
              <tbody>
                {page.data.rows.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTime(row.created_at, locale)}</td>
                    <td>{row.actor_name ?? row.actor_principal_id ?? '—'}</td>
                    <td className="mono">{row.action}</td>
                    <td className="mono">
                      {row.target_type}
                      {row.target_id ? `: ${row.target_id}` : ''}
                    </td>
                    <td>{t(`audit.outcome.${row.outcome}`)}</td>
                    <td className="mono">{row.ip_address ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              total={page.data.total}
              limit={page.data.limit}
              offset={page.data.offset}
              onChange={setOffset}
            />
          </>
        )
      ) : null}
    </section>
  );
}
